import { Link } from 'react-router-dom'
import { ExternalLink, FolderKanban, Inbox, Target } from 'lucide-react'
import type { ClienteAgradaDetail } from '@/services/clientesService'
import { formatFinanceStatus } from '@/hooks/useFinanceiroPage'
import { FinanceDetailSectionCard } from '@/components/Financeiro/FinanceDetailSectionCard'
import { formatCurrency } from '@/utils/format-currency'
import { formatDate, formatDateTime } from '@/utils/format-date'
import { cn } from '@/utils/cn'
import { ClienteAgradaCadastroTab } from './ClienteAgradaCadastroTab'
import { ClienteAgradaHistoricoTab } from './ClienteAgradaHistoricoTab'
import { ClienteAgradaServicosTab } from './ClienteAgradaServicosTab'
import { ClienteAgradaProjectExpand } from './ClienteAgradaProjectExpand'
import { formatDealEtapa } from './clienteAgradaDetailUtils'
import type { ClienteAgradaTabId } from './clienteAgradaDetailTabsConfig'

type Props = {
  detail: ClienteAgradaDetail
  tab: ClienteAgradaTabId
  highlightProjectId?: string | null
  onProjectUpdated?: () => void
}

function getDealValor(valor: unknown): string {
  const n = Number(valor)
  if (!valor || Number.isNaN(n)) return '—'
  return formatCurrency(n)
}

export function ClienteAgradaDetailContent({
  detail,
  tab,
  highlightProjectId,
  onProjectUpdated,
}: Props) {
  if (tab === 'cadastro') {
    return <ClienteAgradaCadastroTab detail={detail} />
  }

  if (tab === 'historico') {
    return <ClienteAgradaHistoricoTab detail={detail} />
  }

  if (tab === 'servicos') {
    return <ClienteAgradaServicosTab detail={detail} />
  }

  if (tab === 'deals') {
    return (
      <FinanceDetailSectionCard title='Deals vinculados' icon={Target} accent='#987de8'>
        {(detail.deals?.length ?? 0) === 0 ? (
          <p className='py-6 text-center text-sm text-text-muted'>Nenhum deal vinculado.</p>
        ) : (
          <ul className='space-y-3'>
            {detail.deals.map((d) => (
              <li
                key={d.id}
                className='rounded-xl border border-white/[0.06] bg-white/[0.03] p-4 transition-colors hover:bg-white/[0.05]'
              >
                <div className='flex flex-wrap items-start justify-between gap-3'>
                  <div className='min-w-0'>
                    <Link
                      to={`/deals?deal=${d.id}`}
                      className='inline-flex items-center gap-1.5 font-semibold text-text-primary hover:text-accent'
                    >
                      {d.nome}
                      <ExternalLink size={12} aria-hidden />
                    </Link>
                    <p className='mt-1 text-sm text-text-muted'>
                      {d.owner?.name ? `Responsável: ${d.owner.name}` : 'Sem responsável'}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                      d.etapa === 'GANHO'
                        ? 'bg-emerald-500/15 text-emerald-400'
                        : 'bg-white/[0.06] text-text-muted',
                    )}
                  >
                    {formatDealEtapa(d.etapa)}
                  </span>
                </div>
                <div className='mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-text-muted'>
                  <span>Valor: {getDealValor(d.valor)}</span>
                  {d.dataFechamento && (
                    <span>Fechamento: {formatDate(d.dataFechamento)}</span>
                  )}
                  <span>Criado: {formatDate(d.createdAt)}</span>
                  <span>Atualizado: {formatDateTime(d.updatedAt)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </FinanceDetailSectionCard>
    )
  }

  if (tab === 'projetos') {
    return (
      <div className='space-y-4'>
        {(detail.financeProjects?.length ?? 0) === 0 ? (
          <FinanceDetailSectionCard title='Projetos financeiros' icon={FolderKanban} accent='#3b82f6'>
            <p className='py-6 text-center text-sm text-text-muted'>Nenhum projeto financeiro.</p>
          </FinanceDetailSectionCard>
        ) : (
          detail.financeProjects.map((p) => {
            const isHighlighted = highlightProjectId === p.id
            return (
              <FinanceDetailSectionCard
                key={p.id}
                title={p.name ?? 'Projeto'}
                icon={FolderKanban}
                accent='#3b82f6'
                className={cn(isHighlighted && 'ring-1 ring-accent/40')}
                headerExtra={
                  <span className='text-xs text-text-muted'>{formatDate(p.createdAt)}</span>
                }
              >
                <div className='mb-3 flex flex-wrap gap-2 text-xs'>
                  <span className='rounded-full bg-white/[0.06] px-2 py-0.5 text-text-primary'>
                    {formatFinanceStatus(p.status)}
                  </span>
                  <span className='rounded-full bg-white/[0.06] px-2 py-0.5 text-text-muted'>
                    {p.source.replace(/_/g, ' ')}
                  </span>
                  {p.deal && (
                    <span className='rounded-full bg-accent/10 px-2 py-0.5 text-accent'>
                      Deal: {p.deal.nome}
                    </span>
                  )}
                </div>
                {(p.requests?.length ?? 0) > 0 ? (
                  <ul className='space-y-2'>
                    {p.requests.map((r) => (
                      <li
                        key={r.id}
                        className='rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5'
                      >
                        <div className='flex flex-wrap items-center justify-between gap-2'>
                          <p className='font-mono text-xs text-text-muted'>{r.protocol}</p>
                          <span className='text-[10px] uppercase text-text-muted'>
                            {formatFinanceStatus(r.status)}
                          </span>
                        </div>
                        <p className='mt-0.5 text-sm font-medium text-text-primary'>
                          {r.type.replace(/_/g, ' ')}
                        </p>
                        <p className='mt-1 text-xs text-text-muted'>
                          Aberto {formatDateTime(r.createdAt)}
                          {r.area ? ` · ${r.area.replace(/_/g, ' ')}` : ''}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className='text-sm text-text-muted'>Sem solicitações vinculadas.</p>
                )}
                {isHighlighted && (
                  <div className='mt-4 border-t border-white/[0.06] pt-4'>
                    <ClienteAgradaProjectExpand projectId={p.id} onUpdated={onProjectUpdated} />
                  </div>
                )}
              </FinanceDetailSectionCard>
            )
          })
        )}
      </div>
    )
  }

  return (
    <FinanceDetailSectionCard title='Chamados financeiros' icon={Inbox} accent='#f59e0b'>
      {(detail.chamados?.length ?? 0) === 0 ? (
        <p className='py-6 text-center text-sm text-text-muted'>Nenhum chamado vinculado.</p>
      ) : (
        <ul className='space-y-3'>
          {detail.chamados.map((c) => (
            <li
              key={c.id}
              className='rounded-xl border border-white/[0.06] bg-white/[0.03] p-4'
            >
              <div className='flex flex-wrap items-start justify-between gap-2'>
                <div>
                  <p className='font-mono text-xs text-text-muted'>{c.protocol}</p>
                  <p className='mt-0.5 font-semibold text-text-primary'>
                    {c.type.replace(/_/g, ' ')}
                  </p>
                </div>
                <span className='rounded-full bg-white/[0.06] px-2 py-0.5 text-[10px] font-semibold uppercase text-text-muted'>
                  {formatFinanceStatus(c.status)}
                </span>
              </div>
              <div className='mt-2 flex flex-wrap gap-x-4 text-xs text-text-muted'>
                <span>Aberto: {formatDateTime(c.createdAt)}</span>
                <span>Atualizado: {formatDateTime(c.updatedAt)}</span>
                {c.area && <span>Área: {c.area.replace(/_/g, ' ')}</span>}
                {c.slaDueAt && <span>SLA: {formatDateTime(c.slaDueAt)}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </FinanceDetailSectionCard>
  )
}
